import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, ArrowLeft, Shield, Zap, BarChart3, User } from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { useSellerAuth } from '../context/SellerAuthContext.js';
import { useProducts } from '../context/ProductContext.js';
import { requestSellerOtp, verifySellerOtp } from '../api/seller.js';
import { Button } from '../components/ui/Button.js';
import { OtpInput } from '../components/ui/OtpInput.js';

type Step = 'form' | 'otp';
type Mode = 'login' | 'register';

const FEATURES = [
  { icon: Zap,       title: 'Instant registration',  desc: 'Pre-register lift serials to customers in seconds.'        },
  { icon: BarChart3, title: 'Live status tracking',   desc: 'Monitor device status and expiry from one dashboard.'     },
  { icon: Shield,    title: 'Secure & compliant',     desc: 'OTP-verified access, end-to-end encrypted connections.'   },
];

export function LoginPage() {
  const { accessToken, login } = useSellerAuth();
  const { hasSelected }        = useProducts();
  const navigate = useNavigate();

  const [mode, setMode]                = useState<Mode>('login');
  const [step, setStep]                = useState<Step>('form');
  const [name, setName]                = useState('');
  const [mobile, setMobile]            = useState('');
  const [otp, setOtp]                  = useState('');
  const [loading, setLoading]          = useState(false);
  const [resendAt, setResendAt]        = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft]  = useState(0);

  useEffect(() => {
    if (accessToken) navigate(hasSelected ? '/dashboard' : '/select-products', { replace: true });
  }, [accessToken, hasSelected, navigate]);

  useEffect(() => {
    if (resendAt === null) return;
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [resendAt]);

  function switchMode(m: Mode) {
    setMode(m);
    setName('');
    setMobile('');
    setOtp('');
    setStep('form');
    setResendAt(null);
  }

  /** Backend requires E.164; accept bare Indian 10-digit input and add +91. */
  function normalizeMobile(raw: string): string {
    const v = raw.replace(/[\s-]/g, '');
    if (/^\d{10}$/.test(v)) return `+91${v}`;
    if (/^91\d{10}$/.test(v)) return `+${v}`;
    return v;
  }

  async function handleRequestOtp(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const normalized = normalizeMobile(mobile);
      setMobile(normalized);
      const res = await requestSellerOtp({
        mobileNumber: normalized,
        ...(mode === 'register' ? { name: name.trim() } : {}),
      });
      setResendAt(new Date(res.resendAvailableAt).getTime());
      setStep('otp');
    } catch (err) {
      toast.error((err as AxiosError<{ message?: string }>).response?.data?.message ?? 'Failed to send OTP.');
    } finally { setLoading(false); }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await verifySellerOtp({
        mobileNumber: mobile,
        otp,
        ...(mode === 'register' ? { name: name.trim() } : {}),
      });
      login(res.accessToken, res.refreshToken, res.seller);
    } catch (err) {
      toast.error((err as AxiosError<{ message?: string }>).response?.data?.message ?? 'Invalid OTP.');
      setOtp('');
    } finally { setLoading(false); }
  }

  async function handleResend() {
    if (secondsLeft > 0) return;
    setLoading(true);
    try {
      const res = await requestSellerOtp({
        mobileNumber: mobile,
        ...(mode === 'register' ? { name: name.trim() } : {}),
      });
      setResendAt(new Date(res.resendAvailableAt).getTime());
      setOtp('');
      toast.success('New OTP sent.');
    } catch (err) {
      toast.error((err as AxiosError<{ message?: string }>).response?.data?.message ?? 'Failed to resend OTP.');
    } finally { setLoading(false); }
  }

  const canSubmit = mode === 'register'
    ? name.trim() !== '' && mobile.trim() !== ''
    : mobile.trim() !== '';

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel — brand ── */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-slate-900 p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm">H</div>
          <span className="font-semibold text-white text-sm">Haron</span>
        </div>

        <div>
          <p className="text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">Seller Portal</p>
          <h1 className="text-4xl font-bold text-white leading-[1.15] mb-5">
            Everything you need<br />to manage your<br />devices.
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed mb-10 max-w-xs">
            Register lift serial numbers, track device status, and manage service requests — all in one place.
          </p>

          <div className="space-y-5">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-200">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-600">© 2026 Haron · Autonomix Innovation</p>
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-90">

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-xs">H</div>
            <span className="font-semibold text-slate-900 text-sm">Haron</span>
          </div>

          <AnimatePresence mode="wait">
            {step === 'form' ? (
              <motion.div
                key={`form-${mode}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                {/* Mode toggle */}
                <div className="flex rounded-xl bg-slate-100 p-1 mb-8">
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                      mode === 'login'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMode('register')}
                    className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-150 ${
                      mode === 'register'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Register
                  </button>
                </div>

                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">
                    {mode === 'login' ? 'Welcome back' : 'Create account'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {mode === 'login'
                      ? 'Enter your mobile number to receive an OTP.'
                      : 'Enter your name and mobile number to get started.'}
                  </p>
                </div>

                <form onSubmit={handleRequestOtp} className="space-y-4">
                  {mode === 'register' && (
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                        Your name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="name"
                          type="text"
                          maxLength={120}
                          placeholder="e.g. Priya Sharma"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                          className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="mobile" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
                      Mobile number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="mobile"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">Include country code — e.g. +91…</p>
                  </div>

                  <Button type="submit" fullWidth loading={loading} disabled={!canSubmit}>
                    Send OTP
                  </Button>

                  {mode === 'login' && (
                    <p className="text-center text-xs text-slate-400">
                      New here?{' '}
                      <button type="button" onClick={() => switchMode('register')} className="text-blue-600 font-semibold hover:underline">
                        Create an account
                      </button>
                    </p>
                  )}
                  {mode === 'register' && (
                    <p className="text-center text-xs text-slate-400">
                      Already have an account?{' '}
                      <button type="button" onClick={() => switchMode('login')} className="text-blue-600 font-semibold hover:underline">
                        Sign in
                      </button>
                    </p>
                  )}
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="otp"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">Verify OTP</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    6-digit code sent to <span className="font-semibold text-slate-700">{mobile}</span>
                  </p>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <OtpInput value={otp} onChange={setOtp} disabled={loading} />

                  {secondsLeft > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Resend available in</span>
                      <span className="font-semibold text-slate-700 tabular-nums">{secondsLeft}s</span>
                    </div>
                  )}

                  <Button type="submit" fullWidth loading={loading} disabled={otp.length !== 6}>
                    Verify &amp; Sign in
                  </Button>

                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      fullWidth
                      onClick={handleResend}
                      disabled={secondsLeft > 0 || loading}
                    >
                      Resend OTP
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      onClick={() => { setStep('form'); setOtp(''); }}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Back
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
